import React, { useEffect, useState } from "react";
import type { Node } from "@xyflow/react";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  InfoWindow,
} from "@react-google-maps/api";
import { Address, NodeData, Event } from "../types/types";
import { Button } from "primereact/button";
import AddressCard from "./AddressCard";

const containerStyle = {
  width: "100%",
  height: "100vh",
};

const fallbackCenter = {
  lat: 56.096995,
  lng: 12.389982,
};

// Move this outside the component so it's not recreated on every render
const GOOGLE_MAP_LIBRARIES: (
  | "places"
  | "drawing"
  | "geometry"
  | "visualization"
)[] = ["places"];

interface WorldMapAreaProps {
  nodes: Node<NodeData>[];
  handleAddUpdateAddress: (
    id: string,
    nodeTitle: string,
    name: string,
    street: string,
    zip_code: string,
    city: string,
    Phone: string,
    email: string,
    vat: string,
    employees: string,
    notes: string,
    latitude?: number,
    longitude?: number,
  ) => Promise<void>;
  handleDeleteAddress: (id: string) => void;
}

const WorldMapArea: React.FC<WorldMapAreaProps> = ({
  nodes,
  handleAddUpdateAddress,
  handleDeleteAddress,
}: WorldMapAreaProps) => {
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [center, setCenter] = useState(fallbackCenter);
  const [showDialog, setShowDialog] = useState(false);
  const [address, setAddress] = useState<Address>();
  const [showInfo, setShowInfo] = useState<number | false>(false);
  const [showGetBackButton, setShowGetBackButton] = useState(false);

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAP_LIBRARIES, // Use the static array here
  });

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCenter({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        () => {
          console.warn(
            "Geolocation failed or was denied. Using fallback center.",
          );
        },
      );
    }
  }, []);

  const geocodeAddress = async (
    address: string,
  ): Promise<google.maps.LatLng | null> => {
    const geocoder = new google.maps.Geocoder();
    try {
      const { results } = await geocoder.geocode({ address });
      if (results[0]) {
        return results[0].geometry.location;
      }
    } catch (error) {
      console.error("Geocoding error:", error);
    }
    return null;
  };

  const [markers, setMarkers] = useState<google.maps.LatLng[]>([]);

  useEffect(() => {
    const fetchMarkers = async () => {
      const locations = await Promise.all(
        nodes.map((node) => {
          if (node.data.address && node.data.address.street) {
            return geocodeAddress(
              `${node.data.address.street}, ${node.data.address.zip_code} ${node.data.address.city}`,
            );
          }
          // Return a LatLng with 0,0 if address is missing
          return new google.maps.LatLng(0, 0);
        }),
      );
      setMarkers(
        locations.filter((loc): loc is google.maps.LatLng => loc !== null),
      );
    };

    if (nodes.length > 0) {
      fetchMarkers();
    }
  }, [nodes]);

  const openDialog = () => {
    setAddress({
      id: `user-${Date.now()}`,
      nodeTitle: "",
      name: "",
      street: "",
      zip_code: "",
      city: "",
      Phone: "",
      email: "",
      vat: "",
      employees: "",
      notes: "",
      latitude: undefined,
      longitude: undefined,
    });
    setShowDialog(true);
  };

  // const handleSaveAddress = () => {
  //   // if (!event.title || !event.date) {
  //   //   return;
  //   // }

  //   // const existing = nodes.find((node) => node.id === event.id);
  //   // if (existing) {
  //   //   handleAddUpdateNode(
  //   //     existing.id,
  //   //     event.title!,
  //   //     event.notes || "",
  //   //     existing.event || {},
  //   //     existing.address || {}
  //   //   );
  //   // } else {
  //   //   handleAddUpdateNode(
  //   //     `user-${Date.now()}`,
  //   //     event.title!,
  //   //     event.notes || "",
  //   //     {},
  //   //     {}
  //   //   );
  //   // }
  //   setShowDialog(false);
  // };

  return (
    <div>
      {isLoaded ? (
        <div style={{ position: "relative", width: "100%", height: "100%" }}>
          {markers.length === 0 && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                zIndex: 2,
                pointerEvents: "none",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "90vh",
              }}
            >
              <h1 style={{ textAlign: "center", color: "#607D8B" }}>
                Click the Add button to define locations
              </h1>
            </div>
          )}
          {/* Button to exit Street View */}
          {map && map.getStreetView().getVisible() && showGetBackButton && (
            <div
              style={{
                position: "absolute",
                top: 80,
                right: 16,
                zIndex: 10,
                background: "white",
                borderRadius: 4,
                boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                padding: "6px 12px",
              }}
            >
              <Button
                label="Back to Map"
                icon="pi pi-map"
                className="p-button-sm p-button-secondary"
                onClick={() => {
                  if (map) {
                    map.getStreetView().setVisible(false);
                    setShowGetBackButton(false);
                  }
                }}
              />
            </div>
          )}
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={center}
            zoom={10}
            onLoad={setMap}
          >
            {markers.map((marker, index) => {
              if (marker.lat() !== 0 && marker.lng() !== 0) {
                const node = nodes[index];
                const address = node?.data.address;
                const addressFields = [
                  address?.name,
                  address?.street,
                  address?.zip_code && address?.city
                    ? `${address.zip_code} ${address.city}`
                    : address?.city || address?.zip_code,
                  address?.Phone,
                  address?.email,
                  address?.vat,
                  address?.employees,
                  address?.notes,
                ].filter((field) => field && field.trim() !== "");
                const addressString =
                  addressFields.length > 0
                    ? addressFields.join(", ")
                    : "Unknown address";
                return (
                  <Marker
                    key={index}
                    position={marker}
                    onClick={() => setShowInfo(index)}
                  >
                    {showInfo === index && (
                      <InfoWindow
                        position={marker}
                        onCloseClick={() => {
                          setShowInfo(false);
                          // Exit Street View if active
                          if (map && map.getStreetView().getVisible()) {
                            map.getStreetView().setVisible(false);
                          }
                        }}
                      >
                        <div
                          style={{
                            background: "white",
                            padding: "6px 12px",
                            borderRadius: "4px",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                            whiteSpace: "nowrap",
                            maxWidth: 300,
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            if (map) {
                              const sv = map.getStreetView();
                              sv.setPosition({
                                lat: marker.lat(),
                                lng: marker.lng(),
                              });
                              sv.setPov({ heading: 70, pitch: 0 });
                              sv.setVisible(true);
                              setShowGetBackButton(true);
                              setShowInfo(false);
                            }
                          }}
                          title="Click to open Street View"
                        >
                          <div style={{ marginBottom: 8 }}>
                            <img
                              src={`https://maps.googleapis.com/maps/api/streetview?size=250x100&location=${marker.lat()},${marker.lng()}&fov=80&heading=70&pitch=0&key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}`}
                              alt="Street View"
                              style={{
                                width: "100%",
                                borderRadius: 4,
                                objectFit: "cover",
                              }}
                            />
                          </div>
                          {addressString}
                          <div
                            style={{
                              color: "#1976d2",
                              marginTop: 6,
                              fontSize: 13,
                            }}
                          >
                            Click to open Street View
                          </div>
                        </div>
                      </InfoWindow>
                    )}
                  </Marker>
                );
              }
              return null;
            })}
          </GoogleMap>
        </div>
      ) : null}
      <Button
        icon="pi pi-plus"
        className="p-button-rounded p-button-primary"
        style={{
          position: "fixed",
          bottom: "2rem",
          right: "2rem",
          zIndex: 1000,
          width: "4.5rem",
          height: "3.5rem",
        }}
        onClick={() => setShowDialog(true)}
        aria-label="Add Location"
      />
      <AddressCard
        visible={showDialog}
        nodeAddress={null}
        setShowAddressCard={setShowDialog}
        handleAddUpdateAddress={handleAddUpdateAddress}
        handleDeleteAddress={handleDeleteAddress}
      />
    </div>
  );
};

export default WorldMapArea;
